import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { FaTimes, FaTrash } from "react-icons/fa";

const Preview = ({ setShowPreview, OpenAiChatTabItems, loading, onDelete }) => {
  // Close on Esc + lock page scroll while modal is open
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setShowPreview(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [setShowPreview]);

  // Small local helper: format dates safely
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

  // Guard array shape
  const items = useMemo(() => Array.isArray(OpenAiChatTabItems) ? OpenAiChatTabItems : [], [OpenAiChatTabItems]);

  // Copy answer (plain text) per message
  const [copiedIdx, setCopiedIdx] = useState(null);
  const copyAnswer = async (html, idx) => {
    try {
      const el = document.createElement("div");
      el.innerHTML = html || "";
      const text = el.textContent || el.innerText || "";
      await navigator.clipboard.writeText(text.trim());
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1200);
    } catch (_) { }
  };

  return (
    <div
      className="fixed inset-0 z-[999999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setShowPreview(false)} // backdrop close
    >
      <div
        className="w-full max-w-3xl h-[85vh] rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()} // prevent close when clicking inside
      >
        {/* Header (sticky) */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 bg-gradient-to-b from-white to-gray-50">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 truncate">Preview</h2>
            <p className="text-xs text-gray-500">{items.length} message{items.length === 1 ? "" : "s"}</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg px-3 py-2 disabled:opacity-60"
              onClick={onDelete}
              aria-label="Delete chat"
              title="Delete chat"
            >
              {loading ? (
                <AiOutlineLoading className="text-sm animate-spin" />
              ) : (
                <FaTrash className="text-sm" />
              )}
              {loading ? "Deleting..." : "Delete Chat"}
            </button>

            <button
              onClick={() => setShowPreview(false)}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-900 text-white hover:bg-gray-800 transition"
              aria-label="Close preview"
              title="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">
          {items.map((tab, index) => (
            <div key={index} className="space-y-2">
              {/* User bubble (question) */}
              <div className="flex flex-col items-end gap-1">
                <div className="max-w-[90%] rounded-2xl rounded-tr-md bg-gray-900 text-white px-4 py-3 shadow-sm">
                  <p className="text-sm leading-relaxed">{tab?.question}</p>
                </div>
                <p className="text-[10px] text-gray-500">{fmt(tab?.createdAt)}</p>
              </div>

              {/* Assistant bubble (answer) */}
              <div className="flex flex-col items-start gap-1">
                <div className="relative group max-w-[95%] w-full rounded-2xl rounded-tl-md bg-gray-50 ring-1 ring-gray-200 px-4 py-3 shadow-sm">
                  {/* Copy button (shows on hover) */}
                  <button
                    type="button"
                    onClick={() => copyAnswer(tab?.answer, index)}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition text-[11px] px-2 py-1 rounded-md bg-white ring-1 ring-gray-200 text-gray-700 hover:bg-gray-50"
                    title="Copy answer"
                  >
                    {copiedIdx === index ? "Copied" : "Copy"}
                  </button>

                  {/* Render HTML safely (as in your original) */}
                  <div
                    className="text-sm leading-relaxed text-gray-900"
                    dangerouslySetInnerHTML={{ __html: tab?.answer }}
                  />
                </div>
                <p className="text-[10px] text-gray-500">{fmt(tab?.createdAt)}</p>
              </div>
            </div>
          ))}

          {items.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="h-2 w-2 rounded-full bg-gray-400 inline-block" />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-gray-800">No chat content</h3>
                <p className="mt-1 text-xs text-gray-500">There’s nothing to preview yet.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Preview;
